import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $profileFeatureStore } from '@lib/store/profile';
import { useAccountFlow, useCurrencySymbol } from '@shared/hooks';
import type { AccountByIdResponse } from '@shared/api/rest/accounts';
import { SidebarItem, SidebarRow } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import SettingIcon from '@assets/icons/setting.svg';
import NotificationIcon from '@assets/icons/notification.svg';

interface IAccountSidebarItemsProps {
  account?: AccountByIdResponse;
  openEditModal: () => void;
  openChangeAlertModal: () => void;
  openEditRecipientsModal: () => void;
}

export const AccountSidebarItems = (props: IAccountSidebarItemsProps) => {
  const { id } = useParams();

  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { accountFlow } = useAccountFlow(profileFeatureStore.data?.accountFlows, id ? parseInt(id) : undefined);
  const symbol = useCurrencySymbol(props.account?.currency);

  if (!props.account) return null;
  return (
    <>
      <SidebarItem title='Account details' icon={<Icon icon={<SettingIcon />} color='inherit' />}>
        <SidebarRow leftRowText='Account owner' rightRowText={props.account.name || 'None'} />
        <SidebarRow leftRowText='Account label' rightRowText={props.account.accountLabel || 'None'} />
        <SidebarRow leftRowText='Cost centre' rightRowText={props.account.costCentre || 'None'} />
        <SidebarRow leftRowText='Account category' rightRowText={props.account.category || 'None'} />
        {(accountFlow?.editAccountCategory || accountFlow?.editAccountLabel || accountFlow?.editCostCentre) && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openEditModal}>
            Edit details
          </Button>
        )}
      </SidebarItem>
      <SidebarItem
        title='Alerts'
        icon={<Icon icon={<NotificationIcon />} color='inherit' />}
        isHidden={!(accountFlow?.accountAlertsSettings.minimalBalance || accountFlow?.accountAlertsSettings.declinedAuths)}
      >
        <SidebarRow
          leftRowText='Minimal available balance'
          isHidden={!accountFlow?.accountAlertsSettings.minimalBalance}
          rightRowText={
            props.account.minimalBalanceAlertSubscription?.minimalBalance
              ? `${symbol}${props.account.minimalBalanceAlertSubscription?.minimalBalance}`
              : 'None'
          }
        />
        {props.account.minimalBalanceAlertSubscription?.recipientDetails?.map((item, idx) => (
          <SidebarRow
            key={idx}
            isHidden={
              !(
                accountFlow?.accountAlertsSettings.minimalBalance &&
                !!props.account?.minimalBalanceAlertSubscription?.recipientDetails?.some((row) => row.email != null || row.firstName != null)
              )
            }
            leftRowText={item.firstName}
            rightRowText={item.email}
          />
        ))}
        {accountFlow?.accountAlertsSettings.minimalBalance && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openChangeAlertModal}>
            Change alert
          </Button>
        )}
        <Text className='mb-5' size='sm'>
          Declined Authorizations
        </Text>
        {props.account.declinedAuthorizationSubscription?.recipientDetails?.map((item, idx) => (
          <SidebarRow
            key={idx}
            isHidden={!accountFlow?.accountAlertsSettings.minimalBalance}
            leftRowText={item.firstName}
            rightRowText={item.email}
          />
        ))}
        {accountFlow?.accountAlertsSettings.declinedAuths && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openEditRecipientsModal}>
            Edit recipients
          </Button>
        )}
      </SidebarItem>
    </>
  );
};
